<template>
  <page-loader>
    <preview-filters
      v-model="query.params"
      :is-disabled="hasPageErrors"
      :is-loading="isLoading"
      :sites="sites"
      button="Get Table"
      @update-filters="onFiltersUpdate"
    />
    <v-container>
      <v-row>
        <v-col>
          <tip-banner v-if="query.params.allowSwaps" hide-title>
            <p>
              By enabling the <code>Allow Swaps</code> property, this table may now present offers that don't directly
              support a cryptocurrency, but allow you to withdraw onto the blockchain that it is on. This is so that we
              can still present users with ways to get newer cryptocurrencies that are only found on decentralised
              exchanges like PancakeSwap and co.
            </p>
            <p>
              If the table is highlighted in
              <span class="deep-purple--text text--accent-4 font-weight-bold">purple</span>, it is presenting you with
              "swap" offers.
            </p>
          </tip-banner>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-text class="py-0">
              <v-row class="my-0">
                <v-col sm="3" class="d-flex align-center py-0">
                  <select-list
                    v-model="shortcodeType"
                    :items="shortcodeTypeItems"
                    class="flex-grow-1"
                    label="Shortcode Type"
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col sm="3" class="d-flex align-center py-0">
                  <text-input
                    v-model="shortcodeText"
                    :disabled="!showShortcodeTextInput"
                    class="flex-grow-1"
                    label="Text"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col sm="3" class="d-flex align-center py-0">
                  <v-btn height="40" color="primary" block depressed @click="copy(shortcode, 'Table Shortcode')">
                    <v-icon left small>file_copy</v-icon>
                    Copy shortcode
                  </v-btn>
                </v-col>
                <v-col sm="3" class="d-flex align-center py-0">
                  <div class="overflow-auto py-4">
                    <base-chip color="grey lighten-2" class="max-width-none px-2">
                      <v-icon class="mr-2">link</v-icon>
                      {{ shortcode }}
                    </base-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-sheet class="pa-1" :color="swapOnly ? 'deep-purple accent-4' : 'white'" rounded>
            <offers-list
              :chips="['promoted', 'ordered', 'deals-value']"
              :filters="query.params"
              :is-loading="isLoading"
              :items="offers"
              :pagination="pagination"
              link="offers"
              public
              show-position
              show-star-rating
              @page-change="onPageChange"
            />
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
// Helpers
import { mapGetters, mapMutations } from 'vuex'
// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
import isSet from '@/utils/is-set'
// Services
import { offers, sites, deals } from '@/services/offers'
// Components
import BaseChip from '@/components/chips/base-chip'
import OffersList from '@/views/offers/components/offers-list'
import PageLoader from '@/components/page-loader'
import PreviewFilters from './components/preview-filters'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input'
import TipBanner from '@/components/tip-banner'

export default {
  name: 'TablePreview',
  components: {
    BaseChip,
    OffersList,
    PageLoader,
    PreviewFilters,
    SelectList,
    TextInput,
    TipBanner,
  },
  mixins: [QueryParamsHandler],
  data: () => ({
    defaultQuery: {
      country: 'GB',
      language: 'en',
      offerType: 'crypto',
      site: null,
      coins: [],
      stocks: [],
      activeDeals: [],
      department: 'organic',
      paymentMethods: [],
      allowSwaps: false,
    },
    isLoading: true,
    offers: [],
    swapOnly: false,
    queriesDefined: [
      'coins',
      'country',
      'language',
      'limit',
      'offerType',
      'paymentMethods',
      'site',
      'stocks',
      'department',
      'allowSwaps',
    ],
    queryDefaults: [
      'coins',
      'country',
      'language',
      'limit',
      'offerType',
      'paymentMethods',
      'site',
      'stocks',
      'department',
      'allowSwaps',
    ],
    queryPagination: false,
    querySearch: false,
    shortcodeText: null,
    shortcodeType: 'inv-table',
    shortcodeTypeItems: [
      { key: 'Default', value: 'inv-table' },
      { key: 'News', value: 'inv-news-table' },
      { key: 'Visit Link', value: 'inv-visit', hasText: true },
    ],
    sites: [],
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['options']),
    ...mapGetters('options', ['getItemsForOption']),
    filteredDeals() {
      return this.activeDeals.reduce((acc, deal) => {
        if (deal.commission_rates[this.query.params.country]) {
          acc.push(Object.assign({}, deal, { commission_rates: deal.commission_rates[this.query.params.country] }))
        }
        return acc
      }, [])
    },
    filteredDealsByPartner() {
      return this.filteredDeals.reduce((acc, deal) => {
        acc[deal.partner] = deal
        return acc
      }, {})
    },
    sitesForDepartment() {
      return this.site.filter(({ department }) => this.query.params.department === department)
    },
    shortcode() {
      const { coins = [], offerType, paymentMethods = [] } = this.query.params
      const filters = [
        coins.length && ['coins', coins.join(',')],
        ['offer-type', offerType],
        paymentMethods.length && ['payment-method', paymentMethods.join(',')],
      ]
        .filter(Boolean)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ')
      const base = `[${this.shortcodeType} ${filters}]`
      const text = this.shortcodeText
      const closingTag = this.shortcodeText ? `[/${this.shortcodeType}]` : null
      return [base, text, closingTag].filter(Boolean).join('')
    },
    showShortcodeTextInput() {
      return !!this.shortcodeTypeItems.find(({ value }) => value === this.shortcodeType)?.hasText
    },
  },
  watch: {
    'query.params.department'(newValue, oldValue) {
      if (!oldValue) return
      this.$set(this.query.params, 'site', null)
    },
    shortcodeType() {
      this.shortcodeText = null
    },
  },
  async created() {
    try {
      await this.getActiveDeals()
      await this.getAllSites()
      await this.getFilteredTable()
    } catch (error) {
      this.setPageErrors(true)
      this.$notify({
        type: 'error',
        text: 'Unable to retrieve Offers Table',
      })
    }
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getAllSites() {
      this.isLoading = true
      try {
        const { data } = await sites.getAllSites()
        this.sites = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch sites',
        })
      }
      this.isLoading = false
    },
    async getActiveDeals() {
      this.isLoading = true
      try {
        const { country, offerType } = this.query.params
        const filters = Object.entries({
          offer_typesContains: offerType,
          countriesContains: country,
          status: 'live',
        }).reduce((acc, [key, value]) => {
          if (isSet(value)) acc[key] = value
          return acc
        }, {})
        const { data } = await deals.getFilteredDeals({ filters })
        this.activeDeals = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve active deals',
        })
      }
      this.isLoading = false
    },
    async getFilteredTable() {
      this.setPageErrors(false)
      this.isLoading = true

      const { offerType, country, language, site, department, allowSwaps } = this.query.params
      const { coinsContains, paymentMethodsContains, stocksContains } = this.queryFilters
      const filters = { coinsContains, paymentMethodsContains, stocksContains }

      const params = {
        offerType,
        country,
        language,
        filters,
        showPromotions: !!site,
        allowSwaps: allowSwaps || undefined,
      }
      const headers = { 'x-request-origin': site || this.getAnySiteOrigin(department) }

      try {
        const { rows, swapOnly } = await offers.getPublicOffers(params, headers)
        this.offers = await Promise.all(rows.map(this.mapOffer))
        this.swapOnly = swapOnly
        this.pagination.total = this.offers.length
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Offer Table',
        })
      }

      this.isLoading = false
    },
    async mapOffer({
      slug,
      type,
      name,
      logoThumbnail,
      starRating,
      published,
      restrictedCountries,
      departments,
      position,
    }) {
      const partners = await offers.getPartnersForOffer(slug)
      const partner = partners?.length === 1 ? partners[0].slug : null
      const deal = partner ? this.filteredDealsByPartner[partner] : null
      const restricted =
        restrictedCountries && !!restrictedCountries.find(country => country === this.query.params.country)
      const restrictedForDepartment = !departments.includes(this.query.params.country)

      return {
        slug,
        type,
        name,
        logoThumbnail: logoThumbnail || null,
        starRating,
        published,
        deal,
        position,
        restricted,
        restrictedForDepartment,
      }
    },
    getAnySiteOrigin(department) {
      const { hostname } = this.sites.find(site => site.department === department) || {}
      return hostname
    },
    onFiltersUpdate() {
      this.getFilteredTable()
    },
    copy(val, text) {
      this.copyToClipboard(val, text)
    },
    copyToClipboard(text, label) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'absolute'
      textArea.style.top = '-100000px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        this.$notify({
          text: `${label} has been copied to your clipboard.`,
        })
      } catch (err) {
        this.$notify({
          type: 'error',
          text: `Unable to copy ${label}`,
        })
      }

      document.body.removeChild(textArea)
    },
    async onPageChange(page) {
      this.pagination.currentPage = page
    },
  },
}
</script>
